import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Form,
  Input,
  Switch,
  List,
  Tag,
  Space,
  Typography,
  Empty,
  Skeleton,
  message,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  FunctionOutlined,
} from "@ant-design/icons";
import { Button, Modal } from "../../../components/Common";
import { useMutations } from "../../../hooks/useApiMutation";
import { useConfirm } from "../../../hooks/useConfirm";

const { Text } = Typography;

const DEFAULT_SCHEMA = JSON.stringify(
  {
    type: "object",
    properties: {
      query: { type: "string", description: "Search query" },
    },
    required: ["query"],
  },
  null,
  2
);

const ToolsTab = ({ agentId, onChanged }) => {
  const { mutate } = useMutations();
  const mutateRef = useRef(mutate);
  mutateRef.current = mutate;
  const { confirmAction } = useConfirm();
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await mutateRef.current("fetchAgentTools", {
        method: "get",
        endpoint: `/v1/agents/${agentId}/tools`,
      });
      setTools(data?.data || []);
    } catch {
      setTools([]);
    } finally {
      setLoading(false);
    }
  }, [agentId]);

  useEffect(() => {
    load();
  }, [load]);

  const onOpen = (tool) => {
    setEditing(tool || null);
    form.resetFields();
    if (tool) {
      form.setFieldsValue(tool);
    } else {
      form.setFieldsValue({
        isEnabled: true,
        parametersJson: DEFAULT_SCHEMA,
      });
    }
    setOpen(true);
  };

  const onSave = async () => {
    try {
      const values = await form.validateFields();
      try {
        JSON.parse(values.parametersJson);
      } catch {
        message.error("ParametersJson must be valid JSON.");
        return;
      }
      await mutate("saveAgentTool", {
        method: "put",
        endpoint: `/v1/agents/${agentId}/tools`,
        payload: values,
        alert: { success: editing ? "Tool updated." : "Tool added." },
      });
      setOpen(false);
      load();
      onChanged?.();
    } catch (err) {
      if (err?.errorFields) return;
      message.error("Failed to save tool");
    }
  };

  return (
    <div>
      <Space
        style={{
          width: "100%",
          justifyContent: "space-between",
          marginBottom: 16,
        }}
      >
        <div>
          <Text strong style={{ fontSize: 16 }}>
            Tools / functions
          </Text>
          <div>
            <Text type="secondary">
              Define JSON-schema tools the agent can invoke at runtime. Maps to
              OpenAI <code>tools[]</code> and Claude <code>tools[]</code>.
            </Text>
          </div>
        </div>
        <Button
          variant="primary"
          icon={<PlusOutlined />}
          onClick={() => onOpen(null)}
        >
          Add tool
        </Button>
      </Space>

      {loading ? (
        <Skeleton active paragraph={{ rows: 3 }} />
      ) : tools.length === 0 ? (
        <Empty description="No tools yet" />
      ) : (
        <List
          itemLayout="horizontal"
          dataSource={tools}
          renderItem={(t) => (
            <List.Item
              actions={[
                <Button
                  variant="ghost"
                  icon={<EditOutlined />}
                  aria-label={`Edit ${t.name}`}
                  onClick={() => onOpen(t)}
                />,
                <Button
                  variant="ghost"
                  danger
                  icon={<DeleteOutlined />}
                  aria-label={`Delete ${t.name}`}
                  onClick={() =>
                    confirmAction({
                      key: "deleteAgentTool",
                      method: "delete",
                      endpoint: `/v1/agents/${agentId}/tools/${t.agentToolId}`,
                      title: "Remove tool",
                      content: `Remove tool "${t.name}"?`,
                      okText: "Remove",
                      okType: "danger",
                      onSuccess: () => {
                        load();
                        onChanged?.();
                      },
                    })
                  }
                />,
              ]}
            >
              <List.Item.Meta
                avatar={
                  <FunctionOutlined
                    style={{ fontSize: 24, color: "var(--accent-blue)" }}
                  />
                }
                title={
                  <Space>
                    <Text strong>{t.name}</Text>
                    {t.isEnabled ? (
                      <Tag color="success">Enabled</Tag>
                    ) : (
                      <Tag>Disabled</Tag>
                    )}
                  </Space>
                }
                description={t.description || <Text type="secondary">No description</Text>}
              />
            </List.Item>
          )}
        />
      )}

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        onConfirm={onSave}
        title={editing ? `Edit tool: ${editing.name}` : "Add tool"}
        confirmText="Save"
        width={720}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="Name"
            rules={[
              { required: true, message: "Name is required" },
              {
                pattern: /^[a-zA-Z_][a-zA-Z0-9_]*$/,
                message: "Use snake_case or camelCase (letters, digits, underscore)",
              },
            ]}
          >
            <Input placeholder="search_web" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} placeholder="What the tool does and when the agent should call it." />
          </Form.Item>
          <Form.Item
            name="parametersJson"
            label="Parameters (JSON Schema)"
            rules={[{ required: true, message: "Schema is required" }]}
          >
            <Input.TextArea
              rows={12}
              style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}
            />
          </Form.Item>
          <Form.Item name="isEnabled" label="Enabled" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ToolsTab;
